import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, Mail, Home, ShoppingBag } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useShopifyCartStore } from '@/stores/shopifyCartStore';
import { SEOHead } from '@/components/SEOHead';
import { trackPurchase } from '@/lib/analytics';

const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const items = useShopifyCartStore((s) => s.items);
  const clearCart = useShopifyCartStore((s) => s.clearCart);
  const orderName = searchParams.get('order') || null;

  const orderValue = items.reduce(
    (sum, item) => sum + parseFloat(item.price.amount) * item.quantity,
    0
  );
  const orderCurrency = items[0]?.price.currencyCode || 'USD';

  useEffect(() => {
    if (orderName) trackPurchase(orderName, orderValue > 0 ? orderValue : undefined, orderCurrency);
    clearCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderName]);

  return (
    <Layout>
      <SEOHead
        title="Order confirmed — Thank you | GizToo"
        description="Your GizToo order has been confirmed. Check your email for the confirmation and tracking details once your package ships."
        canonicalPath="/order-confirmation"
      />
      <div className="container-wide py-12">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Thank you for your order!</h1>
          <p className="text-xl text-muted-foreground mb-8">Your order has been successfully placed</p>
          {orderName && (
            <Card className="mb-8">
              <CardContent className="p-6 text-left">
                <p className="text-sm text-muted-foreground mb-1">Order Number</p>
                <p className="text-xl font-bold text-primary">{orderName}</p>
              </CardContent>
            </Card>
          )}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <Mail className="h-5 w-5 text-primary mt-0.5" />
                <div className="text-left">
                  <p className="font-medium">Confirmation Sent</p>
                  <p className="text-sm text-muted-foreground">We've sent a confirmation email with your order details</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <Package className="h-5 w-5 text-primary mt-0.5" />
                <div className="text-left">
                  <p className="font-medium">Shipment Tracking</p>
                  <p className="text-sm text-muted-foreground">Once your order ships, we'll send you tracking information</p>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/"><Button size="lg" className="gap-2"><Home className="h-4 w-4" />Back to Home</Button></Link>
            <Link to="/catalog"><Button size="lg" variant="outline" className="gap-2"><ShoppingBag className="h-4 w-4" />Continue Shopping</Button></Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OrderConfirmation;